import classNames from 'classnames';
import { type MouseEvent, type PropsWithChildren } from 'react';

import AddButton from './AddButton';
import s from './TableRow.module.scss';

type Props = {
  state?: null | 'success' | 'warning' | 'error' | 'muted' | 'note';
  title?: string;
  className?: string;
  onClick?: (event?: MouseEvent<HTMLTableRowElement>) => void;
  onCmdDoubleClick?: () => void;
} & (
  | {
      rowNumber: number;
      onAdd: (newRowIndex: number) => void;
    }
  | {
      rowNumber?: number;
      onAdd?: never;
    }
);

const TableRow: React.FC<PropsWithChildren<Props>> = ({
  children,
  className,
  rowNumber,
  title,
  state = null,
  onAdd,
  onClick,
  onCmdDoubleClick,
}) => {
  const handleAdd = (): void => {
    if (onAdd) {
      onAdd(rowNumber + 1);
    }
  };
  return (
    <tr
      title={title}
      tabIndex={onClick ? 0 : undefined}
      onClick={(e) => {
        // Skip clicks on interactive elements (buttons, links, inputs, etc.)
        const target = e.target as HTMLElement;
        if (target.closest('button, a, input, select, textarea, [role="button"], [role="option"]')) {
          return;
        }
        if (!e.metaKey && !e.ctrlKey) {
          onClick?.(e);
        }
      }}
      onDoubleClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('button, a, input, select, textarea, [role="button"], [role="option"]')) {
          return;
        }
        e.stopPropagation();
        if (e.metaKey || e.ctrlKey) {
          onCmdDoubleClick?.();
        }
      }}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (document.activeElement !== e.currentTarget) return;

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!e.metaKey && !e.ctrlKey) {
            onClick();
          }
        }
      }}
      className={classNames(
        s.wrapper,
        onClick && s.clickable,
        state === 'success' && s.success,
        state === 'warning' && s.warning,
        state === 'error' && s.error,
        state === 'muted' && s.muted,
        state === 'note' && s.note,
        className,
      )}
    >
      {children}
      {onAdd && <AddButton onClick={handleAdd}></AddButton>}
    </tr>
  );
};

export default TableRow;
